import React, { useEffect, useState, useMemo } from 'react';
import { Card, Row, Col, Form, Button, Table, Pagination, Spinner, Modal } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import axios from 'axios';

const statuses = [
  { value: '', label: 'All Status' },
  { value: 'new', label: 'New Applied' },
  { value: 'registered', label: 'Registered' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'attended', label: 'M&G Attended' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'left', label: 'Left' }
];

const FilteredApplicantsTable = () => {
  const [domains, setDomains] = useState(['All']);
  const [filters, setFilters] = useState({ status: '', domain: '', from_date: null, to_date: null });
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedAts, setSelectedAts] = useState([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editedSubject, setEditedSubject] = useState('');
  const [editedBody, setEditedBody] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [sending, setSending] = useState(false);
  const [dirty, setDirty] = useState(false);

  const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${base}/api/domains`);
        setDomains(['All', ...(res.data || [])]);
      } catch {
        setDomains(['All']);
      }
    })();
    // preload templates for modal usage
    (async () => {
      try {
        const res = await axios.get(`${base}/api/email/templates`, { withCredentials: true });
        setTemplates(res.data.templates || []);
      } catch {
        setTemplates([]);
      }
    })();
  }, []);

  useEffect(() => {
    fetchData(1);
    // eslint-disable-next-line
  }, [filters]);

  const formatDate = (d) => {
    if (!d) return null;
    if (d instanceof Date) return d.toISOString().split('T')[0];
    return d;
  };

  const fetchData = async (p = page) => {
    try {
      setLoading(true);
      const params = {
        page: p,
        limit,
        status: filters.status,
        domain: filters.domain || undefined,
        from_date: formatDate(filters.from_date) || undefined,
        to_date: formatDate(filters.to_date) || undefined
      };
      const res = await axios.get(`${base}/api/applicants/reports/applicants-list`, { params, withCredentials: true });
      const body = res.data || {};
      setRows(body.data || []);
      setTotal(body.pagination?.total || 0);
      setTotalPages(body.pagination?.totalPages || 1);
      setPage(body.pagination?.page || p);
    } catch (e) {
      setRows([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    const params = new URLSearchParams({
      status: filters.status || '',
      domain: filters.domain || '',
      from_date: formatDate(filters.from_date) || '',
      to_date: formatDate(filters.to_date) || ''
    });
    const url = `${base}/api/applicants/reports/export?${params.toString()}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = 'applicants_report.xlsx';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleChange = (k, v) => setFilters(prev => ({ ...prev, [k]: v }));

  const allVisibleSelected = rows.length > 0 && rows.every(r => selectedAts.includes(r.ats_number));
  const toggleSelectAllVisible = (checked) => {
    if (checked) {
      const ids = rows.map(r => r.ats_number);
      setSelectedAts(prev => Array.from(new Set([...prev, ...ids])));
    } else {
      const idsSet = new Set(rows.map(r => r.ats_number));
      setSelectedAts(prev => prev.filter(id => !idsSet.has(id)));
    }
  };
  const toggleRow = (ats, checked) => {
    setSelectedAts(prev => checked ? [...prev, ats] : prev.filter(x => x !== ats));
  };

  // Keep ats_number alongside the address so the backend can log each send
  // against the applicant row.
  const recipients = useMemo(() => {
    const sel = new Set(selectedAts);
    return rows
      .filter(r => sel.has(r.ats_number) && r.email_id)
      .map(r => ({ ats_number: r.ats_number, email: r.email_id }));
  }, [selectedAts, rows]);

  const recipientEmails = useMemo(() => recipients.map(r => r.email), [recipients]);

  const onOpenEmailModal = () => {
    setShowEmailModal(true);
  };
  const onCloseEmailModal = () => {
    setShowEmailModal(false);
    setSelectedTemplate(null);
    setEditedSubject('');
    setEditedBody('');
    setDirty(false);
  };
  const onTemplateSelect = (id) => {
    setSelectedTemplate(id);
    const tpl = templates.find(t => t.id === id);
    if (tpl) {
      setEditedSubject(tpl.subject || '');
      setEditedBody(tpl.body || '');
      setDirty(false);
    } else {
      setEditedSubject('');
      setEditedBody('');
      setDirty(false);
    }
  };
  const saveCurrentTemplate = async () => {
    if (!selectedTemplate) return;
    try {
      setSavingTemplate(true);
      await axios.put(`${base}/api/email/templates/${selectedTemplate}`, { subject: editedSubject, body: editedBody }, { withCredentials: true });
      const res = await axios.get(`${base}/api/email/templates`, { withCredentials: true });
      setTemplates(res.data.templates || []);
      setDirty(false);
    } catch (e) {
      console.error('Save template failed', e.message);
    } finally {
      setSavingTemplate(false);
    }
  };
  const sendEmails = async () => {
    if (!selectedTemplate || recipientEmails.length === 0) return;
    try {
      setSending(true);
      if (dirty) {
        try {
          await axios.put(`${base}/api/email/templates/${selectedTemplate}`, { subject: editedSubject, body: editedBody }, { withCredentials: true });
          setDirty(false);
        } catch (e) { console.warn('Auto-save failed', e.message); }
      }
      await axios.post(`${base}/api/email/send-domain`, {
        domain: filters.domain || 'General',
        template_id: selectedTemplate,
        ats_numbers: recipients.map(r => r.ats_number),
        email_list: recipientEmails
      }, { withCredentials: true });
      onCloseEmailModal();
    } catch (e) {
      console.error('Send emails failed', e.message);
    } finally {
      setSending(false);
    }
  };

  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    pages.push(
      <Pagination.Item key={i} active={i === page} onClick={() => fetchData(i)}>
        {i}
      </Pagination.Item>
    );
  }

  return (
    <Card className="mb-4">
      <Card.Header>
        <h5 className="mb-0">Applicants Report</h5>
      </Card.Header>
      <Card.Body>
        <Row className="g-3 mb-3">
          <Col md={3}>
            <Form.Group>
              <Form.Label>Status</Form.Label>
              <Form.Select value={filters.status} onChange={e => handleChange('status', e.target.value)}>
                {statuses.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Domain</Form.Label>
              <Form.Select value={filters.domain || 'All'} onChange={e => handleChange('domain', e.target.value === 'All' ? '' : e.target.value)}>
                {domains.map(d => <option key={d} value={d}>{d}</option>)}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>From</Form.Label>
              <DatePicker selected={filters.from_date} onChange={d => handleChange('from_date', d)} className="form-control" dateFormat="yyyy-MM-dd" />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>To</Form.Label>
              <DatePicker selected={filters.to_date} onChange={d => handleChange('to_date', d)} className="form-control" dateFormat="yyyy-MM-dd" />
            </Form.Group>
          </Col>
        </Row>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="text-muted">Total: {total}</div>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={() => { setFilters({ status: '', domain: '', from_date: null, to_date: null }); }}>Clear</Button>
            <Button variant="success" onClick={exportExcel} disabled={!rows.length}>Export Excel</Button>
            <Button variant="primary" disabled={!selectedAts.length} onClick={onOpenEmailModal}>Email Selected ({selectedAts.length})</Button>
          </div>
        </div>
        <div className="table-responsive">
          <Table striped bordered hover size="sm">
            <thead>
              <tr>
                <th>
                  <Form.Check
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={e => toggleSelectAllVisible(e.target.checked)}
                  />
                </th>
                <th>ATS</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Domain</th>
                <th>Source</th>
                <th>Applied</th>
                <th>Created</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="text-center"><Spinner animation="border" size="sm" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={10} className="text-center text-muted">No data</td></tr>
              ) : rows.map(r => (
                <tr key={r.ats_number} className={selectedAts.includes(r.ats_number) ? 'table-active' : ''}>
                  <td>
                    <Form.Check
                      type="checkbox"
                      checked={selectedAts.includes(r.ats_number)}
                      onChange={e => toggleRow(r.ats_number, e.target.checked)}
                    />
                  </td>
                  <td>{r.ats_number}</td>
                  <td>{r.first_name} {r.last_name}</td>
                  <td>{r.email_id}</td>
                  <td>{r.country_code} {r.phone_number}</td>
                  <td>{r.domain}</td>
                  <td>{r.source}</td>
                  <td>{r.application_date}</td>
                  <td>{r.created_at}</td>
                  <td>
                    {r.rejected ? 'Rejected' : r.left ? 'Left' : r.assigned_project ? 'Assigned' : r.registered_startupworld ? 'Registered' : r.attended_meet_greet ? 'Attended' : 'New'}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        {totalPages > 1 && (
          <div className="d-flex justify-content-center">
            <Pagination size="sm">{pages}</Pagination>
          </div>
        )}
      </Card.Body>
      {/* Email Modal */}
      <Modal show={showEmailModal} onHide={onCloseEmailModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Email Selected Applicants</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mb-3">
            <Col md={8}>
              <Form.Group>
                <Form.Label>Template</Form.Label>
                <Form.Select value={selectedTemplate || ''} onChange={e => onTemplateSelect(Number(e.target.value))}>
                  <option value="">Select a template</option>
                  {templates.map(t => (
                    <option key={t.id} value={t.id}>{t.template_name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4} className="d-flex align-items-end">
              <div className="small text-muted w-100">Recipients: {recipientEmails.length}</div>
            </Col>
          </Row>
          {selectedTemplate && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Subject</Form.Label>
                <Form.Control
                  type="text"
                  value={editedSubject}
                  onChange={e => { setEditedSubject(e.target.value); setDirty(true); }}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Body</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={8}
                  value={editedBody}
                  onChange={e => { setEditedBody(e.target.value); setDirty(true); }}
                  style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                />
                <div className="mt-2 d-flex justify-content-between align-items-center">
                  <small className="text-muted">{dirty ? 'Unsaved changes' : 'Saved'}</small>
                  <Button
                    variant="outline-success"
                    size="sm"
                    disabled={!selectedTemplate || savingTemplate || !dirty}
                    onClick={saveCurrentTemplate}
                  >{savingTemplate ? 'Saving...' : 'Save Template'}</Button>
                </div>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onCloseEmailModal}>Close</Button>
          <Button
            variant="primary"
            disabled={!selectedTemplate || !recipientEmails.length || sending}
            onClick={sendEmails}
          >{sending ? 'Sending...' : `Send Email (${recipientEmails.length})`}</Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default FilteredApplicantsTable;
